import React, { useEffect, useRef, useState } from 'react';

import { Button, Modal, Steps, message } from 'antd';
import { T } from 'react-translator-component';

import { buildPayload, fieldKind, validate, visibleFields } from './buildPayload';
import DynamicField from './DynamicField';
import {
  AttachmentRow,
  ErrorText,
  FieldGroup,
  FieldLabel,
  FullWidthSelect,
  RightSpacedButton,
  SpacedAlert,
  SpacedSteps,
} from './styled';

import {
  createTicket,
  getChildTenants,
  getRequestTypeFields,
  getRequestTypes,
  uploadTempAttachment,
} from '../../../hooks/jira';
import { ChildTenant, RequestField, RequestType } from '../../../types/responses/Jira';

// Os tipos do antd 4.10 com @types/react 18 nao declaram `children` em Modal/Steps.
const AntModal = Modal as React.FC<React.ComponentProps<typeof Modal> & { children?: React.ReactNode }>;
const AntSteps = SpacedSteps as React.FC<React.ComponentProps<typeof Steps> & { children?: React.ReactNode }>;

interface Props {
  visible: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const LINK_FAILED =
  'The ticket may have been created but could not be linked to your company. Do not try again; contact support.';

interface AttachmentEntry {
  uid: number;
  name: string;
  status: 'uploading' | 'done' | 'error';
  id?: string;
}

const NewTicketModal = ({ visible, onClose, onCreated }: Props) => {
  const [step, setStep] = useState(0);
  const [types, setTypes] = useState<RequestType[]>([]);
  const [typeId, setTypeId] = useState<string>();
  const [fields, setFields] = useState<RequestField[]>([]);
  const [values, setValues] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attachments, setAttachments] = useState<AttachmentEntry[]>([]);
  const [kids, setKids] = useState<ChildTenant[]>([]);
  const [onBehalf, setOnBehalf] = useState<number>();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');
  // 409 no create: o chamado pode existir sem vinculo; nao oferecer nova tentativa ate reabrir.
  const [locked, setLocked] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const fieldsCall = useRef(0);
  const busyRef = useRef(false);
  const session = useRef(0);
  const uidSeq = useRef(0);

  useEffect(() => {
    if (!visible) return undefined;
    session.current += 1;
    const current = session.current;
    busyRef.current = false;
    setBusy(false);
    setStep(0);
    setTypeId(undefined);
    setFields([]);
    setValues({});
    setErrors({});
    setAttachments([]);
    setOnBehalf(undefined);
    setFailure('');
    setLocked(false);
    setLoadingFields(false);
    fieldsCall.current += 1;
    setTypes([]);
    setKids([]);
    getRequestTypes()
      .then((r) => current === session.current && setTypes(r))
      .catch(() => current === session.current && setFailure('Could not load request types'));
    getChildTenants()
      .then((r) => current === session.current && setKids(r || []))
      .catch(() => current === session.current && setKids([]));
    return () => {
      session.current += 1;
    };
  }, [visible]);

  const chooseType = async (id: string) => {
    const current = session.current;
    fieldsCall.current += 1;
    const call = fieldsCall.current;
    setTypeId(id);
    setFailure('');
    setLoadingFields(true);
    try {
      const loaded = visibleFields(await getRequestTypeFields(id));
      if (current !== session.current || call !== fieldsCall.current) return;
      setFields(loaded);
      setValues({});
      setErrors({});
      setStep(1);
      setLoadingFields(false);
    } catch {
      if (current !== session.current || call !== fieldsCall.current) return;
      setTypeId(undefined);
      setLoadingFields(false);
      setFailure('Could not load form fields');
    }
  };

  const next = () => {
    const errs = validate(fields, values, attachments.filter((a) => a.status === 'done').length);
    setErrors(errs);
    if (Object.keys(errs).length === 0) setStep(2);
  };

  const patchAttachment = (uid: number, patch: Partial<AttachmentEntry>) =>
    setAttachments((list) => list.map((a) => (a.uid === uid ? { ...a, ...patch } : a)));

  const onFile = async (file: File) => {
    const current = session.current;
    uidSeq.current += 1;
    const uid = uidSeq.current;
    setAttachments((list) => [...list, { uid, name: file.name, status: 'uploading' }]);
    try {
      const id = await uploadTempAttachment(file);
      if (current === session.current) patchAttachment(uid, { status: 'done', id });
    } catch {
      if (current === session.current) {
        patchAttachment(uid, { status: 'error' });
        setFailure('Upload failed');
      }
    }
  };

  const removeAttachment = (uid: number) => setAttachments((list) => list.filter((a) => a.uid !== uid));

  const uploading = attachments.some((a) => a.status === 'uploading');

  const submit = async () => {
    if (busyRef.current || uploading || locked || !typeId) return;
    busyRef.current = true;
    setBusy(true);
    setFailure('');
    const current = session.current;
    const ids = attachments.filter((a) => a.status === 'done' && a.id).map((a) => a.id as string);
    try {
      await createTicket(
        buildPayload(
          typeId,
          values,
          ids,
          onBehalf,
          Object.fromEntries(fields.map((f) => [f.fieldId, fieldKind(f)])),
        ),
      );
      message.success(T('Ticket created'));
      onCreated();
      if (current === session.current) onClose();
    } catch (e: any) {
      if (current === session.current) {
        const code = e?.response?.status;
        if (code === 409) {
          setLocked(true);
          setFailure(LINK_FAILED);
        } else {
          setFailure(code === 403 ? 'You do not have access to this area' : 'Could not create the ticket');
        }
      }
    } finally {
      if (current === session.current) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };

  const reviewValue = (f: RequestField): string => {
    const v = values[f.fieldId];
    if (fieldKind(f) === 'select') return f.validValues.find((o) => o.value === v)?.label ?? String(v);
    return String(v);
  };

  return (
    <AntModal visible={visible} onCancel={onClose} footer={null} width={640} title={T('Open ticket')} destroyOnClose>
      <AntSteps current={step} size="small">
        <Steps.Step title={T('Type')} />
        <Steps.Step title={T('Details')} />
        <Steps.Step title={T('Ticket review step')} />
      </AntSteps>
      {failure && <SpacedAlert type="error" message={T(failure)} />}

      {step === 0 && (
        <>
          <FieldLabel htmlFor="ticket-request-type">{T('Type')}</FieldLabel>
          <FullWidthSelect
            id="ticket-request-type"
            placeholder={T('Choose a request type')}
            value={typeId}
            loading={loadingFields}
            onChange={chooseType}
            options={types.map((t) => ({ value: t.id, label: t.name }))}
          />
        </>
      )}

      {step === 1 && (
        <>
          {kids.length > 0 && (
            <FieldGroup>
              <FieldLabel htmlFor="ticket-on-behalf">{T('Which company is this ticket about?')}</FieldLabel>
              <FullWidthSelect
                id="ticket-on-behalf"
                allowClear
                placeholder={T('My own company (default)')}
                value={onBehalf}
                onChange={(v) => setOnBehalf(v as number | undefined)}
                options={kids.map((k) => ({ value: k.tenantPk, label: k.name }))}
              />
            </FieldGroup>
          )}
          {fields.map((f) => (
            <React.Fragment key={f.fieldId}>
              <DynamicField
                field={f}
                value={values[f.fieldId]}
                error={errors[f.fieldId]}
                onChange={(v) => setValues((s) => ({ ...s, [f.fieldId]: v }))}
                onFile={onFile}
              />
              {fieldKind(f) === 'attachment' &&
                attachments.map((a) => (
                  <AttachmentRow key={a.uid}>
                    <span>{a.name}</span>
                    {a.status === 'uploading' && <span> ({T('Uploading')}...)</span>}
                    {a.status === 'error' && <ErrorText> ({T('Upload failed')})</ErrorText>}
                    <Button type="link" size="small" onClick={() => removeAttachment(a.uid)}>
                      {T('Remove')}
                    </Button>
                  </AttachmentRow>
                ))}
            </React.Fragment>
          ))}
          <Button type="primary" onClick={next}>
            {T('Next')}
          </Button>
        </>
      )}

      {step === 2 && (
        <>
          {fields
            .filter((f) => !['attachment', 'user'].includes(fieldKind(f)) && values[f.fieldId])
            .map((f) => (
              <p key={f.fieldId}>
                <strong>{f.name}:</strong> {reviewValue(f)}
              </p>
            ))}
          {attachments.filter((a) => a.status === 'done').map((a) => (
            <p key={a.uid}>{a.name}</p>
          ))}
          <RightSpacedButton onClick={() => setStep(1)}>{T('Back')}</RightSpacedButton>
          <Button type="primary" loading={busy} disabled={uploading || locked} onClick={submit}>
            {T('Submit')}
          </Button>
        </>
      )}
    </AntModal>
  );
};

export default NewTicketModal;
